import React, { useState } from "react";
import useReportListLogic, { Report, REPORT_STATUS } from "./Reports.logic";
import { DataTable, DataTableFilterMeta } from "primereact/datatable";
import { Column } from "primereact/column";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputText } from "primereact/inputtext";
import { FilterMatchMode } from "primereact/api";
import { ConfirmPopup } from "primereact/confirmpopup";
import { formatDate } from "../../../utils/dateUtils";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { RadioButton } from "primereact/radiobutton";
import { InputTextarea } from "primereact/inputtextarea";

const AdminReportList: React.FC = () => {
  const {
    reports,
    loading,
    reportLoading,
    showDialog,
    hideDialog,
    showReportDialog,
    setReportForm,
    reportForm,
    onInputChange,
    submitted,
    handleReportEvent,
  } = useReportListLogic();

  const statusBodyTemplate = (rowData: Report) => {
    return rowData.status == "resolved" ? (
      <span className="p-tag p-tag-success">{rowData.status_label}</span>
    ) : rowData.status == "rejected" ? (
      <span className="p-tag p-tag-danger">{rowData.status_label}</span>
    ) : (
      <span className="p-tag p-tag-info">{rowData.status_label}</span>
    );
  };

  const [globalFilterValue, setGlobalFilterValue] = useState<string>("");

  const [filters, setFilters] = useState<DataTableFilterMeta>({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    name: { value: null, matchMode: FilterMatchMode.STARTS_WITH },
  });

  const onGlobalFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    let _filters = { ...filters };

    // @ts-ignore
    _filters["global"].value = value;

    setFilters(_filters);
    setGlobalFilterValue(value);
  };

  const renderHeader = () => {
    return (
      <>
        <ConfirmPopup />
        <div className="flex justify-content-start align-items-center mb-2 flex-column-responsive">
          <IconField iconPosition="left">
            <InputIcon className="fas fa-search" />
            <InputText
              value={globalFilterValue}
              onChange={onGlobalFilterChange}
              placeholder="Keyword Search"
            />
          </IconField>
        </div>
      </>
    );
  };

  const header = renderHeader();

  const eventBodyTemplate = (rowData: Report) => {
    return (
      <div>
        <div>
          <strong>{rowData.Event.title}</strong>
        </div>
        <small className="text-muted">
          {formatDate(rowData.Event.datetime)} | {rowData.Event.location_city}
        </small>
      </div>
    );
  };

  const dialogFooter = (
    <React.Fragment>
      <div className="col-xl-12 d-flex align-items-center justify-content-center">
        <Button
          label="Cancel"
          icon="pi pi-times"
          outlined
          onClick={hideDialog}
          disabled={reportLoading}
          className="thm-black-btn small_btn"
        />
        <Button
          label={reportLoading ? "Submitting..." : "Submit Action"}
          icon={reportLoading ? "pi pi-spin pi-spinner" : "pi pi-check"}
          onClick={handleReportEvent}
          disabled={reportLoading}
          className="thm-btn small_btn"
        />
      </div>
    </React.Fragment>
  );

  const actionBodyTemplate = (rowData: Report) => {
    return (
      <>
        {rowData.status === "pending" && (
          <Button
            icon={"fas fa-hand-point-up"}
            rounded
            onClick={() => showDialog(rowData.id, rowData.Event.id)}
            tooltip={"Update Report Status"}
            tooltipOptions={{ position: "top" }}
            disabled={showReportDialog}
          ></Button>
        )}
      </>
    );
  };

  return (
    <>
      <section className="schedule-three ngo_list_wrapper">
        <div className="container">
          <div className="schedule-three__top">
            <div
              className="section-title text-center wow fadeInLeft"
              data-wow-delay="100ms"
            >
              <h2 className="section-title__title">Reported Issues</h2>
            </div>
          </div>
          <div className="schedule-three__bottom">
            <DataTable
              value={reports}
              removableSort
              sortField="createdAt"
              sortOrder={-1}
              showGridlines
              paginator
              rows={5}
              loading={loading}
              rowsPerPageOptions={[5, 10, 25, 50]}
              filters={filters}
              // filterDisplay="row"
              globalFilterFields={[
                "Event.title",
                "reportedHost.name",
                "reported_type_label",
                "reason",
                "comment",
                "status_label",
              ]}
              header={header}
              emptyMessage="No reported issues found."
              onFilter={(e) => setFilters(e.filters)}
              paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
              currentPageReportTemplate="Showing {first} to {last} of {totalRecords} Reported Issues"
              tableStyle={{ minWidth: "50rem" }}
            >
              <Column
                field="Event.title"
                header="Event"
                body={eventBodyTemplate}
                sortable
              ></Column>
              <Column
                field="reportedHost.name"
                header="Event Host"
                sortable
              ></Column>
              <Column
                field="reported_type_label"
                header="Reported For"
                sortable
              ></Column>
              <Column
                field="reason"
                header="Reason for Report"
                sortable
              ></Column>
              <Column field="comment" header="Comment" sortable></Column>
              <Column
                headerStyle={{ width: "10%", minWidth: "8rem" }}
                field="status_label"
                header="Status"
                bodyStyle={{ textAlign: "center" }}
                body={statusBodyTemplate}
                headerClassName="custom-status-column"
              ></Column>
              <Column
                field="createdAt"
                header="Reported At"
                body={(rowData: Report) => rowData.reported_at_formatted}
                sortable
              ></Column>
              <Column
                field="reporter.name"
                header="Reported By"
                sortable
              ></Column>
              <Column
                headerStyle={{ width: "15%", minWidth: "8rem" }}
                bodyStyle={{ textAlign: "center" }}
                headerClassName="custom-status-column"
                header="Actions"
                body={actionBodyTemplate}
              ></Column>
            </DataTable>
          </div>
        </div>
      </section>
      <Dialog
        visible={showReportDialog}
        style={{ width: "32rem" }}
        breakpoints={{ "960px": "75vw", "641px": "90vw" }}
        header={"Update Report Status"}
        modal
        className="p-fluid"
        footer={dialogFooter}
        onHide={hideDialog}
        draggable={false}
      >
        <div className="field mb-3">
          <label className="font-bold mb-2 d-block">Status</label>

          <div className="d-flex gap-4">
            <div className="d-flex align-items-center">
              <RadioButton
                inputId="report-event"
                name="status"
                value={REPORT_STATUS.RESOLVED}
                onChange={(e) =>
                  setReportForm({ ...reportForm, status: e.value })
                }
                checked={reportForm.status === REPORT_STATUS.RESOLVED}
              />
              <label htmlFor="report-event" className="ml-2">
                Resolve
              </label>
            </div>

            <div className="d-flex align-items-center">
              <RadioButton
                inputId="report-host"
                name="status"
                value={REPORT_STATUS.REJECTED}
                onChange={(e) =>
                  setReportForm({ ...reportForm, status: e.value })
                }
                checked={reportForm.status === REPORT_STATUS.REJECTED}
              />
              <label htmlFor="report-host" className="ml-2">
                Reject
              </label>
            </div>
          </div>
        </div>

        {/* Comment */}
        <div className="field">
          <label htmlFor="comment" className="font-bold">
            Comment
          </label>
          <InputTextarea
            id="comment"
            value={reportForm.comment}
            onChange={(e) => onInputChange(e, "comment")}
            placeholder="Please provide details about your action."
            rows={3}
            autoResize
          />
          {submitted && !reportForm.comment && (
            <small className="p-error">Comment is required.</small>
          )}
        </div>
      </Dialog>
    </>
  );
};

export default AdminReportList;
